import React, { Suspense, useCallback, useRef } from 'react';
import { Events, Properties } from '../../../enums/Analytics';
import { getDifferenceInSeconds, getEpochFromString } from '../../../lib/Date';
import { getDocumentLabel } from '../../../Services/Content/Content';
import { isPSPDFKitViewerEnabled } from './Strategies/PSPDFKit/helper';
import { OrganizationFlagEnum } from '../../../lib/features/FeatureFlag';
import { PDFViewerProps } from './Strategies/PDFViewerProps';
import Analytics from '../../../tools/Analytics';
import PDFjsViewer from './Strategies/PDFjs/PDFjsViewer';
import PSPDFKitViewer from './Strategies/PSPDFKit/PSPDFKitViewer';
import useFeatureFlag from '../../../lib/hook/useFeatureFlag';

enum PDFViewerEnum {
  PSPDFKit = 'PSPDFKit',
  PDFjs = 'PDFjs',
}

const PDFViewer = React.forwardRef<HTMLDivElement, PDFViewerProps>(
  (props: PDFViewerProps, ref): JSX.Element => {
    const isNewPdfViewerEnabled = useFeatureFlag(
      OrganizationFlagEnum.NEW_PDF_VIEWER
    );

    const { contentId, createdAt, document, onDownload, trackingProperties } =
      props;

    const { mime, url } = document;

    const { current: isPSPDFKitEnabled } = useRef(
      isPSPDFKitViewerEnabled(isNewPdfViewerEnabled)
    );

    const pdfViewer = isPSPDFKitEnabled
      ? PDFViewerEnum.PSPDFKit
      : PDFViewerEnum.PDFjs;

    const handleDownload = useCallback(() => {
      if (onDownload) {
        onDownload();
      }

      window.open(url, '_blank');

      Analytics.track(Events.CONTENT_DOWNLOADED, {
        ...trackingProperties,
        [Properties.PDF_VIEWER]: pdfViewer,
      });
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [onDownload, trackingProperties, url]);

    const onLoadError = () => {
      const createdAtEpoch = getEpochFromString(createdAt)! * 1000;
      const time = `${getDifferenceInSeconds(Date.now(), createdAtEpoch)}`;

      Analytics.track(Events.CONTENT_PREVIEW_FAILED, {
        ...trackingProperties,
        [Properties.CONTENT_ID]: contentId,
        [Properties.DOCUMENT_TYPE]: getDocumentLabel(mime, 'Unknown'),
        [Properties.PDF_VIEWER]: pdfViewer,
        [Properties.TIME_SINCE_UPLOAD]: time,
      });
    };

    const renderViewer = () => {
      if (isPSPDFKitEnabled) {
        return (
          <PSPDFKitViewer
            {...props}
            onDownload={handleDownload}
            onLoadError={onLoadError}
            ref={ref}
          />
        );
      }

      return (
        <PDFjsViewer
          {...props}
          onDownload={handleDownload}
          onLoadError={onLoadError}
          ref={ref}
        />
      );
    };

    return <Suspense fallback={<></>}>{renderViewer()}</Suspense>;
  }
);

export default PDFViewer;
